// Library Methods
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

// Constants
import {
  DeviceStagesTimeline,
  examCardEnums,
  ExamCardSectionNameType,
  eyeTypeEnums,
} from "src/constants/enums";

// Types
import { DeviceType } from "src/services/devices/types";
import { ExamSectionType, ExamType } from "src/services/exams/types";

// Utils
import { isMultiExamPackage } from "src/utils/exams/examReportsHelper";

export const useExamStatus = () => {
  const { t } = useTranslation();
  const { LEFT } = eyeTypeEnums;

  const getDeviceStageStatus = useCallback(
    (device: DeviceType) => {
      const deviceStageType = device?.deviceStatus?.stage?.stageType;

      if (deviceStageType === "PatientLogin") {
        return {
          status: t("Patient Login"),
          timelineStage: DeviceStagesTimeline.PatientLogin,
        };
      } else if (deviceStageType === "Confirmation") {
        return {
          status: t("Confirmation"),
          timelineStage: DeviceStagesTimeline.Confirmation,
        };
      } else if (deviceStageType === "Tutorial") {
        return {
          status: t("Tutorial"),
          timelineStage: DeviceStagesTimeline.Tutorial,
          extraWord: t("In Progress"),
        };
      } else if (
        deviceStageType === "InAppCalibration" ||
        deviceStageType === "ExternalCalibration"
      ) {
        return {
          status: t("Calibration"),
          timelineStage: DeviceStagesTimeline.Calibration,
        };
      } else if (deviceStageType === "Pause") {
        return {
          status: t("Paused"),
          timelineStage: device?.deviceStatus?.stage?.metadata?.isTutorial
            ? DeviceStagesTimeline.Tutorial
            : DeviceStagesTimeline.Exam,
        };
      } else if (deviceStageType === "Exam") {
        return {
          status: t("In Progress"),
          timelineStage: DeviceStagesTimeline.Exam,
        };
      } else {
        return {
          status: t("Not Started"),
          timelineStage: undefined,
        };
      }
    },
    [t]
  );

  const getMultiPackageStatus = useCallback(
    (
      associatedExam: ExamType,
      currentExamSectionsKey: Array<ExamCardSectionNameType>
    ) => {
      let result: { status?: string; timelineStage?: number } = {
        status: undefined,
        timelineStage: undefined,
      };

      const examSections = currentExamSectionsKey
        .map((sectionName) => associatedExam?.[sectionName])
        .flat();

      const isNotStarted = !examSections.some((examSection) =>
        Boolean(examSection?.completionDate)
      );
      const isCompleted = examSections.every((examSection) =>
        Boolean(examSection?.completionDate)
      );
      const isInvalid = examSections.some(
        (examSection) => examSection?.completionState === "Invalid"
      );
      const isTerminated = examSections.some(
        (examSection) => examSection?.completionState === "Terminated"
      );

      if (isNotStarted) {
        result = {
          status: t("Not Started"),
          timelineStage: undefined,
        };
      } else if (isCompleted) {
        result = {
          status: t("Completed"),
          timelineStage: DeviceStagesTimeline.Completed,
        };
      } else if (isTerminated) {
        result = {
          status: t("Terminated"),
          timelineStage: DeviceStagesTimeline.Terminated,
        };
      } else if (isInvalid) {
        result = {
          status: t("Invalid"),
          timelineStage: DeviceStagesTimeline.Invalid,
        };
      } else {
        const completeSections = examSections.filter((examSection) =>
          Boolean(examSection?.completionDate)
        );
        result = {
          status: `${completeSections?.length} / ${examSections?.length} ${t("Completed")}`,
          timelineStage: DeviceStagesTimeline.Terminated,
        };
      }

      return result;
    },
    [t]
  );

  const getSingleSectionStatus = useCallback(
    (associatedExamSections: Array<ExamSectionType>) => {
      const completionStateStatus = {
        Undefined: t("Not Started"),
        Completed: t("Completed"),
        Terminated: t("Terminated"),
        Invalid: t("Invalid"),
      };

      const completionState = associatedExamSections?.[0]
        ?.completionState as keyof typeof completionStateStatus;

      return {
        status: completionStateStatus?.[completionState],
        timelineStage: DeviceStagesTimeline[completionState],
      };
    },
    [t]
  );

  const getMultiSectionStatus = useCallback(
    (associatedExamSections: Array<ExamSectionType>) => {
      let result: { status?: string; timelineStage?: number } = {
        status: undefined,
        timelineStage: undefined,
      };

      const firstStatus = associatedExamSections?.[0]?.completionState;
      const secondStatus = associatedExamSections?.[1]?.completionState;

      const isTerminated = associatedExamSections.some(
        (examSection) => examSection.completionState === "Terminated"
      );
      const isInvalid = associatedExamSections.some(
        (examSection) => examSection.completionState === "Invalid"
      );
      const isCompleted = associatedExamSections.every(
        (examSection) => examSection.completionState === "Completed"
      );

      // logic if both sections are completed
      if (
        associatedExamSections?.[0]?.completionDate &&
        associatedExamSections?.[1]?.completionDate
      ) {
        if (isTerminated) {
          result = { ...result, status: t("Terminated") };
        } else if (isInvalid) {
          result = { ...result, status: t("Invalid") };
        } else if (isCompleted) {
          result = { ...result, status: t("Completed") };
        }
      }
      // logic if both sections aren't completed
      else if (isTerminated) {
        result = {
          status: t("Terminated"),
          timelineStage: DeviceStagesTimeline.Terminated,
        };
      } else if (isInvalid) {
        result = { ...result, status: t("Invalid") };
      }
      // logic if 1st section is complete and the other isn't terminated/invalid
      else if (firstStatus === "Completed") {
        result = {
          ...result,
          status:
            associatedExamSections?.[0]?.eye === LEFT
              ? t("Left Eye Completed")
              : t("Right Eye Completed"),
        };
      }
      // logic if 2nd section is complete and the other isn't terminated/invalid
      else if (secondStatus === "Completed") {
        result = {
          ...result,
          status:
            associatedExamSections?.[1]?.eye === LEFT
              ? t("Left Eye Completed")
              : t("Right Eye Completed"),
        };
      }
      // logic if the test hasn't started
      else {
        result = { ...result, status: t("Not Started") };
      }

      return result;
    },
    [t, LEFT]
  );

  const getExamStatus = useCallback(
    ({ device, exam }: { device?: DeviceType; exam?: ExamType }) => {
      if (
        exam === undefined &&
        (device === undefined || !device?.associatedExam?.exam)
      ) {
        return undefined;
      }

      const deviceStageType = device?.deviceStatus?.stage?.stageType;
      const associatedExam = device?.associatedExam?.exam ?? exam;

      // get the status from the device stage
      if (deviceStageType) {
        return getDeviceStageStatus(device);
      }
      // get the status from the linked exam
      else if (associatedExam) {
        const examSectionsKeys: Array<ExamCardSectionNameType> = Object.values(
          examCardEnums
        ).map((enumValue) => enumValue.sectionName);

        const currentExamSectionsKey: Array<ExamCardSectionNameType> =
          examSectionsKeys?.filter((key) => {
            const examSections = associatedExam?.[key];
            return examSections && examSections?.length > 0;
          });

        // logic for multi-package exams
        if (isMultiExamPackage(associatedExam)) {
          return getMultiPackageStatus(associatedExam, currentExamSectionsKey);
        }

        const sectionKey = currentExamSectionsKey[0];
        const associatedExamSections = associatedExam?.[sectionKey];

        // logic for exam type with 1 section
        if (associatedExamSections && associatedExamSections?.length === 1) {
          return getSingleSectionStatus(associatedExamSections);
        }

        // logic for exam type with 2 sections
        else if (associatedExamSections && associatedExamSections?.length > 1) {
          return getMultiSectionStatus(associatedExamSections);
        }

        return {
          status: undefined,
          timelineStage: undefined,
          extraWord: undefined,
        };
      }
    },
    [
      getDeviceStageStatus,
      getMultiPackageStatus,
      getMultiSectionStatus,
      getSingleSectionStatus,
    ]
  );

  return { getExamStatus };
};
